import { badgesFor, renderBadges } from './product-badges';
import type { ShopProduct } from '../../commerce/shopify.types';

export interface ProductHover {
  element: HTMLElement;
  show(product: ShopProduct, anchor: HTMLElement): void;
  hide(): void;
  destroy(): void;
}

/**
 * Aperçu au survol : le titre, le prix et les repères du catalogue, sans avoir
 * à ouvrir la fiche. Purement informatif, donc masqué aux lecteurs d'écran :
 * les mêmes informations sont dans la fiche, elle-même accessible au clavier.
 */
export function createProductHover(parent: HTMLElement): ProductHover {
  const element = document.createElement('div');
  element.className = 'product-hover';
  element.setAttribute('aria-hidden', 'true');
  element.hidden = true;
  parent.append(element);

  let timer: number | undefined;

  function hide(): void {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    element.hidden = true;
    element.classList.remove('is-visible');
  }

  function show(product: ShopProduct, anchor: HTMLElement): void {
    const badges = badgesFor(product.details);
    element.replaceChildren();

    const titre = document.createElement('p');
    titre.className = 'product-hover__title';
    titre.textContent = product.title;

    const prix = document.createElement('p');
    prix.className = 'product-hover__price';
    const montant = Number.parseFloat(product.price.amount);
    prix.textContent = Number.isNaN(montant)
      ? ''
      : new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: product.price.currencyCode || 'EUR',
        }).format(montant);

    element.append(titre, prix);
    if (badges.length) element.append(renderBadges(badges));

    element.hidden = false;

    // Placement après affichage : la taille de la carte n'est connue qu'une fois rendue.
    const cible = anchor.getBoundingClientRect();
    const carte = element.getBoundingClientRect();
    const marge = 12;
    let left = cible.left + cible.width / 2 - carte.width / 2;
    left = Math.max(marge, Math.min(left, window.innerWidth - carte.width - marge));
    let top = cible.top - carte.height - marge;
    // Pas la place au-dessus : on bascule sous la vignette.
    if (top < marge) top = Math.min(cible.bottom + marge, window.innerHeight - carte.height - marge);

    element.style.left = `${Math.round(left)}px`;
    element.style.top = `${Math.round(top)}px`;
    requestAnimationFrame(() => element.classList.add('is-visible'));
  }

  return {
    element,
    show(product, anchor) {
      if (timer !== undefined) window.clearTimeout(timer);
      // Petite temporisation : survoler une étagère ne doit pas faire clignoter
      // une carte à chaque produit traversé.
      timer = window.setTimeout(() => show(product, anchor), 180);
    },
    hide,
    destroy(): void {
      hide();
      element.remove();
    },
  };
}
